import type { PresentationSection, QuoteFile } from 'paintscout'
import { matchImageByContext } from './matchImagesByContext'
import { type CategoryPools, imageKey, type PlacedImages, takeFromCategoryPool } from './photo-categories'
import {
  SCRAPED_IMAGE_REF_KEY,
  type ScrapedImageRefFiles,
  type ScrapedImageSlot,
  scrapedImageRefOf
} from './scrapedImageRef'

const COLUMNS = ['left', 'center', 'right'] as const

/**
 * Hands out `images` in order. With `placed`, it skips photos already used elsewhere in the same
 * presentation (e.g. by a category-tagged slot) and records each one it hands out. It repeats a
 * photo only when every one left in `images` is already placed (#24057).
 *
 * `consumed` is how far into `images` the cursor moved, skipped entries included — the caller's
 * running offset has to move past them too.
 */
function createImageCursor(images: QuoteFile[], placed?: PlacedImages) {
  let index = 0
  return {
    next(): QuoteFile | undefined {
      if (placed) {
        for (let i = index; i < images.length; i++) {
          if (!placed.has(imageKey(images[i]))) {
            index = i + 1
            placed.add(imageKey(images[i]))
            return images[i]
          }
        }
      }
      const image = images[index]
      index++
      if (image) placed?.add(imageKey(image))
      return image
    },
    get consumed() {
      return index
    }
  }
}

export function replaceSectionImages(args: {
  section: PresentationSection
  images: QuoteFile[]
  categoryPools?: CategoryPools
  /** Shared across one presentation walk so no photo is placed twice. Omit for a single block. */
  placed?: PlacedImages
  /** The files imported for the generator's `scrapedImageRef`s — see `pick` below. */
  refFiles?: ScrapedImageRefFiles
}): {
  section: PresentationSection
  imageCount: number
} {
  let { section } = args
  const { images, categoryPools, placed, refFiles } = args

  // `images` empty (e.g. no default images configured) previously spun forever here — concatenating
  // an empty array onto itself never grows past length 0, so the loop below never reached 6.
  let loopedImages = [...images]
  while (images.length > 0 && loopedImages.length < 6) {
    loopedImages = [...loopedImages, ...images]
  }

  const cursor = createImageCursor(loopedImages, placed)
  /**
   * One photo for one slot, best evidence first (#24101):
   * 1. the file imported for the slot's own `scrapedImageRef` — the copy model saw that photo sit
   *    with this item's text on the company's site;
   * 2. a photo whose scraped context text matches the slot's text — evidence about this specific
   *    slot, unlike the category enum, which two unrelated photos can share;
   * 3. a same-category photo (the vision classifier's read of the photo itself);
   * 4. the next unplaced photo in order (prefab stock sits at the end of `images`).
   * Steps 1–3 draw from outside the looped sequence, so they never move the cursor — or the
   * caller's offset into `images` would skip a photo it never used.
   */
  const pick = (slot: unknown, category?: Parameters<typeof takeFromCategoryPool>[1], slotText?: string) => {
    const refFile = refFiles?.get(scrapedImageRefOf(slot) ?? '')
    if (refFile) {
      placed?.add(imageKey(refFile))
      return refFile
    }
    return (
      matchImageByContext(images, slotText, placed) ??
      takeFromCategoryPool(categoryPools, category, placed) ??
      cursor.next()
    )
  }
  /** The slot with its photo merged in, minus the generation-only ref. */
  const fill = (slot: unknown, category?: Parameters<typeof takeFromCategoryPool>[1], slotText?: string) => {
    const image = pick(slot, category, slotText)
    const { [SCRAPED_IMAGE_REF_KEY]: _ref, ...rest } = (slot ?? {}) as ScrapedImageSlot
    return { ...rest, ...image } as QuoteFile
  }

  if (['banner', 'image', 'logo'].includes(section.type)) {
    if (section.type === 'image' && section.fields.image?.format === 'youtube') {
      return {
        section,
        imageCount: 0
      }
    }
    const image = fill(section.fields.image, section.fields.imageCategory, section.fields.content)
    return {
      section: { ...section, fields: { ...section.fields, image } },
      imageCount: cursor.consumed
    }
  }

  if (['2-column', '3-column'].includes(section.type)) {
    // An image column's subject is whatever the text column(s) beside it say.
    const columnText = COLUMNS.filter((side) => section.fields[`${side}Type`] === 'text')
      .map((side) => section.fields[`${side}Text`])
      .filter((text): text is string => typeof text === 'string')
      .join(' ')
    COLUMNS.forEach((side) => {
      if (section.fields[`${side}Type`] !== 'image') return
      const image = fill(section.fields[`${side}Image`], section.fields[`${side}ImageCategory`], columnText)
      section = { ...section, fields: { ...section.fields, [`${side}Image`]: image } }
    })
    return { section, imageCount: cursor.consumed }
  }

  if (['cards'].includes(section.type)) {
    section.fields.cards.forEach((card, index) => {
      if (card.file?.format === 'pdf') return
      section.fields.cards[index].file = fill(card.file, card.imageCategory, card.content)
    })
    return { section, imageCount: cursor.consumed }
  }

  if (['gallery'].includes(section.type)) {
    section.fields.files.forEach((file: QuoteFile, index: number) => {
      section.fields.files[index] = fill(file)
    })
    return { section, imageCount: cursor.consumed }
  }

  if (section.type === 'mission') {
    return {
      section: {
        ...section,
        fields: { ...section.fields, image: fill(section.fields.image, undefined, section.fields.content) }
      },
      imageCount: cursor.consumed
    }
  }

  if (section.type === 'services') {
    section.fields.rows.forEach((row, index) => {
      const rowText = [row.title, row.content].filter(Boolean).join(' ')
      section.fields.rows[index] = { ...row, image: fill(row.image, undefined, rowText) }
    })
    return { section, imageCount: cursor.consumed }
  }

  if (section.type === 'slider-image') {
    const leftImage = fill(section.fields.leftImage)
    const rightImage = fill(section.fields.rightImage)
    return {
      section: { ...section, fields: { ...section.fields, leftImage, rightImage } },
      imageCount: cursor.consumed
    }
  }

  // `awards` (badge/certification logos) and `documents` (real PDFs) need specific real-world
  // assets the AI can't fabricate from a generic photo pool — see x-blockGuide `sourceFromWebsite`
  // in presentation-v2.schema.json. Filling their image slots with an unrelated house photo would
  // be actively wrong, not just unfilled, so they're deliberately left untouched here.
  return { section, imageCount: 0 }
}
